import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CaptchaWidget, type CaptchaWidgetHandle } from '../components/CaptchaWidget';
import { authorizeCode, endUserLogin, getApiBase, type OAuthProvider } from '../lib/api';
import { useBranding } from '../lib/branding';
import { isCaptchaRequired, useCaptchaConfig } from '../lib/captcha';
import AuthLayout from './AuthLayout';

const PROVIDER_LABELS: Record<OAuthProvider, string> = {
  google: 'Google',
  github: 'GitHub',
  microsoft: 'Microsoft',
  okta: 'Okta',
  apple: 'Apple',
};

// Build the URL the OAuth-provider button navigates to. The API endpoint
// kicks off the IdP redirect dance and ultimately bounces the user back to
// the consumer's `redirect_uri` with `?code=...&state=...`, so we forward
// the same hosted-login flow params we'd send to /login.
function oauthStartUrl(flow: FlowParams, provider: OAuthProvider): string {
  const params = new URLSearchParams({
    redirect_uri: flow.redirectUri,
    state: flow.state,
    code_challenge: flow.codeChallenge,
    code_challenge_method: flow.codeChallengeMethod,
  });
  return `${getApiBase()}/api/auth/projects/${flow.clientId}/${provider}?${params.toString()}`;
}

type Status = 'idle' | 'submitting' | 'error';

// Required hosted-login flow params, validated upfront from the query string.
// Names mirror RFC 6749 / hosted-login.mdx wire contract.
interface FlowParams {
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

type FlowParamsValidation = { ok: true; params: FlowParams } | { ok: false; reason: string };

function validateFlowParams(searchParams: URLSearchParams): FlowParamsValidation {
  const clientId = searchParams.get('client_id') ?? '';
  const redirectUri = searchParams.get('redirect_uri') ?? '';
  const state = searchParams.get('state') ?? '';
  const codeChallenge = searchParams.get('code_challenge') ?? '';
  const codeChallengeMethod = searchParams.get('code_challenge_method') ?? '';

  const missing: string[] = [];
  if (!clientId) missing.push('client_id');
  if (!redirectUri) missing.push('redirect_uri');
  if (!state) missing.push('state');
  if (!codeChallenge) missing.push('code_challenge');
  if (!codeChallengeMethod) missing.push('code_challenge_method');

  if (missing.length > 0) {
    return {
      ok: false,
      reason: `Missing required parameters: ${missing.join(', ')}.`,
    };
  }

  // PKCE method: hosted-login refuses `plain` from new clients per the wire
  // contract. The API still accepts it for legacy OAuth provider flows, but
  // browsers redirected here with `plain` indicate a misconfigured SDK.
  if (codeChallengeMethod !== 'S256') {
    return {
      ok: false,
      reason: `Unsupported code_challenge_method "${codeChallengeMethod}". Hosted login requires S256.`,
    };
  }

  return {
    ok: true,
    params: { clientId, redirectUri, state, codeChallenge, codeChallengeMethod },
  };
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const validation = useMemo(() => validateFlowParams(searchParams), [searchParams]);
  const { branding } = useBranding();
  const authMethods = branding.authMethods;
  const clientIdForCaptcha = validation.ok ? validation.params.clientId : null;
  const { status: captchaStatus, config: captchaConfig } = useCaptchaConfig(clientIdForCaptcha);
  const captchaRequired = isCaptchaRequired(captchaConfig, 'end_user_login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  if (!validation.ok) {
    return (
      <AuthLayout
        title="We can't sign you in here"
        subtitle="This sign-in link is missing or has invalid parameters. Return to the app that sent you here and try again."
      >
        <p className="auth-error" role="alert">
          {validation.reason}
        </p>
      </AuthLayout>
    );
  }

  const flow = validation.params;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (captchaRequired && !captchaToken) {
      setStatus('error');
      setError('Please complete the verification challenge.');
      return;
    }

    setStatus('submitting');
    setError(null);

    // Step 1: collect a bearer token via password login.
    const loginResult = await endUserLogin(
      flow.clientId,
      email.trim(),
      password,
      captchaToken ?? undefined
    );

    if (!loginResult.success) {
      // Token is single-use on the server — if a CAPTCHA-bearing request
      // failed for any reason (bad password OR captcha rejected), force a
      // fresh challenge before the next submit.
      if (captchaRequired) {
        setCaptchaToken(null);
        captchaRef.current?.reset();
      }
      setStatus('error');
      setError(loginResult.error ?? 'Invalid email or password.');
      return;
    }

    // MFA round-trip: hand off to /mfa/challenge with the project's mfa_token
    // and the original hosted-login flow params. The challenge page verifies
    // the second factor, gets a fresh bearer, and exchanges it for the
    // authorize_code itself — so this component is done once it navigates.
    if (loginResult.mfa_required) {
      const mfaToken = loginResult.data?.mfa_token;
      const mfaMethods = loginResult.data?.mfa_methods ?? [];

      if (!mfaToken) {
        setStatus('error');
        setError('MFA challenge could not be initiated. Please try again.');
        return;
      }

      const challengeQuery = new URLSearchParams({
        project_id: flow.clientId,
        mfa_token: mfaToken,
        methods: mfaMethods.join(','),
        redirect_uri: flow.redirectUri,
        state: flow.state,
        code_challenge: flow.codeChallenge,
        code_challenge_method: flow.codeChallengeMethod,
      });
      navigate(`/mfa/challenge?${challengeQuery.toString()}`);
      return;
    }

    const accessToken = loginResult.access_token;
    if (!accessToken) {
      setStatus('error');
      setError('Sign-in succeeded but no access token was returned. Please try again.');
      return;
    }

    // TODO(hosted-login): wire up magic-link and OAuth provider login flows.
    // The same authorize_code exchange pattern applies — once we have a bearer
    // token, the rest of this function is identical regardless of how the
    // user authenticated.

    // Step 2: exchange the in-memory bearer for a single-use authorization
    // code bound to the consumer's redirect_uri + PKCE challenge. The bearer
    // never leaves this component — it is NOT persisted to localStorage,
    // sessionStorage, or cookies.
    const codeResult = await authorizeCode(flow.clientId, accessToken, {
      redirect_uri: flow.redirectUri,
      state: flow.state,
      code_challenge: flow.codeChallenge,
      code_challenge_method: flow.codeChallengeMethod,
    });

    if (!codeResult.success || !codeResult.code) {
      // Per wire contract: on invalid_redirect_uri (or any code-issuance
      // failure), surface in-page rather than redirecting — we don't trust
      // the URI yet, and we don't want a bad URI to receive an error code.
      setStatus('error');
      setError(
        codeResult.error_description ??
          codeResult.error ??
          'Could not complete sign-in. Please try again.'
      );
      return;
    }

    // Step 3: hand the code+state to the consumer via full-page navigation.
    // window.location.replace prevents a back-button bounce that would re-
    // present the (now-consumed) login form.
    const url = new URL(flow.redirectUri);
    url.searchParams.set('code', codeResult.code);
    url.searchParams.set('state', codeResult.state ?? flow.state);
    window.location.replace(url.toString());
  }

  const oauthProviders = authMethods.oauth_providers;
  const showPasswordForm = authMethods.email_password;
  const hasAnyMethod = showPasswordForm || oauthProviders.length > 0;

  const subtitle = showPasswordForm
    ? 'Use your email and password to continue.'
    : 'Continue with one of the options below.';

  return (
    <AuthLayout title="Sign in" subtitle={subtitle}>
      {!hasAnyMethod ? (
        <p className="auth-error" role="alert">
          No sign-in methods are enabled for this project. Please contact the app owner.
        </p>
      ) : null}

      {oauthProviders.length > 0 ? (
        <div className="auth-oauth-list">
          {oauthProviders.map(provider => (
            <a key={provider} className="auth-oauth-button" href={oauthStartUrl(flow, provider)}>
              Continue with {PROVIDER_LABELS[provider]}
            </a>
          ))}
        </div>
      ) : null}

      {showPasswordForm && oauthProviders.length > 0 ? (
        <div className="auth-divider" role="separator">
          <span>or</span>
        </div>
      ) : null}

      {showPasswordForm ? (
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <label className="auth-field">
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              disabled={status === 'submitting'}
            />
          </label>

          <label className="auth-field">
            <span>Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              disabled={status === 'submitting'}
            />
          </label>

          {captchaRequired && captchaConfig?.provider && captchaConfig.site_key ? (
            <CaptchaWidget
              ref={captchaRef}
              provider={captchaConfig.provider}
              siteKey={captchaConfig.site_key}
              action="end_user_login"
              onVerify={token => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
          ) : null}

          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="auth-submit"
            disabled={
              status === 'submitting' ||
              captchaStatus === 'pending' ||
              email.trim().length === 0 ||
              password.length === 0 ||
              (captchaRequired && !captchaToken)
            }
          >
            {status === 'submitting' ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      ) : null}
    </AuthLayout>
  );
}
